import Add16 from '@carbon/icons-react/es/add/16';
import Delete16 from '@carbon/icons-react/es/delete/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Money16 from '@carbon/icons-react/es/money/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import { ConfigProvider, Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import {
  FinancialNotificationEventType,
  ThresholdConfig,
} from 'api/settings-financials/types';
import { Button } from 'components/atoms/button';
import { Popover } from 'components/atoms/popover';
import { UserTag } from 'components/atoms/select';
import { Tag } from 'components/atoms/tag';
import { Action } from 'components/molecules/action-footer';
import { ConfirmationPopover } from 'components/molecules/action-panel/confirmation-popover';
import { toastService } from 'components/molecules/toast';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import {
  FinancialNotificationsConfigQueryKey,
  useFinancialNotificationsConfigMutation,
} from 'queries/financial-notifications';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { NotifyPeopleTypes, SetupAlertDrawer } from './setup-alert-drawer';
import {
  AlertInfoWrapper,
  AlertTable,
  AlertTitle,
  DeleteIcon,
  EditIcon,
  EmptyStateWrapper,
  TableHeader,
  UpdateIconsWrapper,
  UsersWrapper,
  Wrapper,
} from './styles';

const DeleteConfirmation = styled(ConfirmationPopover)`
  .ant-btn-link {
    color: var(--error-color);
  }
`;

const AlertDefinitionRenderer = ({
  data,
  type,
}: {
  data: ThresholdConfig;
  type: FinancialNotificationEventType;
}) => {
  const translate = useTranslate();
  const userMap = useGetAllActiveUsersMap();

  const usersToShow = data.notifyUsers.slice(0, 2).map((userId) => {
    return userMap[userId];
  });

  const extraCount = data.notifyUsers.length - 2;

  return (
    <Wrapper>
      <AlertInfoWrapper>
        {translate(`settings.financials.${type}.alert.notify`, {
          value: <strong>{`${data.thresholdValue}%`}</strong>,
        })}
      </AlertInfoWrapper>

      <UsersWrapper>
        {data.notifyProjectOwners ? (
          <UserTag
            color="green"
            label={translate('settings.financials.setup_alert.project_owner')}
          />
        ) : null}
        {data.notifyProjectMembers ? (
          <UserTag
            color="gray"
            label={translate('settings.financials.setup_alert.team_members')}
          />
        ) : null}
        {usersToShow.map((user) => (
          <UserTag imageUrl={user?.photoUrl} label={fullName(user)} />
        ))}

        {extraCount > 0 ? (
          <Tag color={getRandomColor(extraCount)}>
            {translate('editor.plus_n_more', {
              count: extraCount,
            })}
          </Tag>
        ) : null}
      </UsersWrapper>
    </Wrapper>
  );
};

const UpdateAlertRenderer = ({
  data,
  allAlerts,
  type,
  configId,
}: {
  configId: number;
  data: ThresholdConfig;
  allAlerts: ThresholdConfig[];
  type: FinancialNotificationEventType;
}) => {
  const [visible, setVisible] = useState(false);
  const [isDrawerVisible, setDrawerVisiblity] = useState(false);
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { mutate } = useFinancialNotificationsConfigMutation(configId, {
    onSuccess: () => {
      setVisible(false);
      queryClient.refetchQueries(FinancialNotificationsConfigQueryKey());
      toastService({
        title: translate('settings.financials.alert.action.success.title'),
        message: translate('settings.financials.alert.delete.success.message'),
      });
    },
  });
  const onDeleteConfirmClick = useCallback(() => {
    const payload = {
      accountNotificationConfigurationMeta: {
        projectThresholdNotificationConditions: allAlerts.filter(
          (alert) => alert.thresholdId !== data.thresholdId,
        ),
      },
    };

    mutate(payload);
  }, [allAlerts, data.thresholdId, mutate]);

  const usersMap = useGetAllActiveUsersMap();
  const deleteAction: Action = useMemo(
    () => ({
      title: <FormattedMessage id="delete" />,
      onClick: onDeleteConfirmClick,
    }),
    [onDeleteConfirmClick],
  );
  const cancelAction: Action = useMemo(
    () => ({
      title: <FormattedMessage id="cancel" />,
      onClick: () => setVisible(false),
    }),
    [],
  );
  const editAction = () => setDrawerVisiblity(true);

  const intialValues = useMemo(() => {
    const notify = [] as NotifyPeopleTypes[];

    if (data.notifyProjectOwners) {
      notify.push(NotifyPeopleTypes.NotifyProjectOwners);
    }
    if (data.notifyProjectMembers) {
      notify.push(NotifyPeopleTypes.NotifyProjectMembers);
    }

    const users = data.notifyUsers.map((userId) => {
      return usersMap[userId];
    });

    const userOptions = users.length
      ? users
          .map((u) => ({
            value: u?.userId,
            label: fullName(u),
            imageUrl: u?.photoUrl,
            description: u?.role?.roleName,
            color: getRandomColor(u?.userId),
            roleId: u?.role?.roleId,
            userType: u?.userType,
            extra: {
              id: u?.userId,
              type: 'user',
              timeOffDetails: u?.userTimeOffDetails,
            },
          }))
          .sort(byLabel)
      : [];

    return {
      notifyPeople: notify,
      notifyUsers: userOptions,
      thresholdValue: data.thresholdValue,
    };
  }, [
    data.notifyProjectMembers,
    data.notifyProjectOwners,
    data.notifyUsers,
    data.thresholdValue,
    usersMap,
  ]);

  return (
    <>
      <UpdateIconsWrapper>
        <Tooltip placement="bottom" title={translate('edit')}>
          <EditIcon>
            <Edit16 onClick={editAction} />
          </EditIcon>
        </Tooltip>
        <Tooltip
          placement="bottom"
          title={translate(
            'settings.financials.delete.alert' as TranslationKey,
          )}
        >
          <Popover
            visible={visible}
            onVisibleChange={setVisible}
            trigger="click"
            placement="bottomRight"
            content={
              <DeleteConfirmation
                title={<FormattedMessage id="delete" />}
                confirmationText={translate(
                  'settings.financials.delete.alert.confirm',
                )}
                ok={deleteAction}
                cancel={cancelAction}
                reverseCta
              />
            }
          >
            <DeleteIcon>
              <Delete16 />
            </DeleteIcon>
          </Popover>
        </Tooltip>
      </UpdateIconsWrapper>
      <SetupAlertDrawer
        configId={configId}
        intialValues={intialValues}
        updateThresholdId={data.thresholdId}
        alerts={allAlerts}
        type={type}
        visible={isDrawerVisible}
        onClose={() => setDrawerVisiblity(false)}
      />
    </>
  );
};

const AlertInfoTable = styled(AlertTable)`
  ${EditIcon} {
    visibility: hidden;
  }
  ${DeleteIcon} {
    visibility: hidden;
  }

  .ant-table-row:hover {
    ${EditIcon} {
      visibility: visible;
    }
    ${DeleteIcon} {
      visibility: visible;
    }
  }
`;

export const AlertInfo: FC<{
  type: FinancialNotificationEventType;
  configId: number;
  alerts: ThresholdConfig[];
}> = ({ type, alerts, configId }) => {
  const translate = useTranslate();
  const [isAlertDrawerVisible, setAlertDrawerVisiblity] = useState(false);

  const openDrawer = () => setAlertDrawerVisiblity(true);

  const columns = [
    {
      key: 'alertDefinition',
      render: (record) => {
        return <AlertDefinitionRenderer type={type} data={record} />;
      },
    },

    {
      key: 'alertEditor',
      render: (record) => {
        return (
          <UpdateAlertRenderer
            configId={configId}
            data={record}
            allAlerts={alerts}
            type={type}
          />
        );
      },
    },
  ];

  return (
    <>
      <TableHeader>
        <AlertTitle>
          {type ===
          FinancialNotificationEventType.ProjectBudgetThresholdReached ? (
            <Money16 />
          ) : (
            <Timer16 />
          )}
          {translate(`settings.financials.${type}.alert`)}
        </AlertTitle>

        <Button
          onClick={openDrawer}
          type="link"
          iconPlacement="right"
          icon={<Add16 />}
        >
          {translate('settings.financials.add.new.alert')}
        </Button>
      </TableHeader>
      <ConfigProvider
        renderEmpty={() => (
          <EmptyStateWrapper>
            {translate('settings.financials.alerts.empty')}
          </EmptyStateWrapper>
        )}
      >
        <AlertInfoTable
          dataSource={alerts}
          columns={columns}
          showHeader={false}
          pagination={false}
        />
      </ConfigProvider>

      <SetupAlertDrawer
        type={type}
        alerts={alerts}
        configId={configId}
        visible={isAlertDrawerVisible}
        onClose={() => setAlertDrawerVisiblity(false)}
      />
    </>
  );
};
